import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Phone, MapPin, Shield, Truck, Cross, Clock, AlertTriangle, Building2, Ship } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageProvider';

const getEmergencyContacts = (t) => [
  {
    id: 'police',
    name: t('usefulInfoPolice'),
    number: '100',
    icon: Shield,
    color: 'bg-blue-500',
    description: t('usefulInfoPoliceDesc')
  },
  {
    id: 'medical_emergency',
    name: t('usefulInfoMedicalEmergency'),
    number: '166',
    icon: Cross,
    color: 'bg-red-500',
    description: t('usefulInfoMedicalEmergencyDesc')
  },
  {
    id: 'fire',
    name: t('usefulInfoFireDept'),
    number: '199',
    icon: Truck,
    color: 'bg-red-500',
    description: t('usefulInfoFireDeptDesc')
  },
  {
    id: 'forest_fire',
    name: t('usefulInfoForestFire'),
    number: '191',
    icon: Truck,
    color: 'bg-orange-500',
    description: t('usefulInfoForestFireDesc')
  },
  {
    id: 'coast_guard',
    name: t('usefulInfoCoastGuard'),
    number: '108',
    icon: Ship,
    color: 'bg-cyan-600',
    description: t('usefulInfoCoastGuardDesc')
  },
  {
    id: 'european_emergency',
    name: t('usefulInfoEuropeanEmergency'),
    number: '112',
    icon: AlertTriangle,
    color: 'bg-purple-600',
    description: t('usefulInfoEuropeanEmergencyDesc')
  },
  {
    id: 'tourist_police',
    name: t('usefulInfoTouristPolice'),
    number: '2810 283 190',
    icon: Shield,
    color: 'bg-indigo-500',
    description: t('usefulInfoTouristPoliceDesc')
  }
];

const getLocalServices = (t) => [
  {
    id: 'regional_health_center',
    name: t('usefulInfoRegionalHealth'),
    number: '28950 340000',
    address: 'Ano Viannos',
    icon: Cross,
    color: 'bg-red-500',
    description: t('usefulInfoRegionalHealthDesc'),
    hours: t('usefulInfoHoursWeekdays')
  },
  {
    id: 'police_viannos',
    name: t('usefulInfoPoliceViannos'),
    number: '28950 22840',
    address: 'Viannos',
    icon: Shield,
    color: 'bg-blue-600',
    description: t('usefulInfoPoliceViannosDesc'),
    hours: t('usefulInfoHours247')
  },
  {
    id: 'municipality',
    name: t('usefulInfoMunicipality'),
    number: '2895 340 100',
    address: 'Viannos',
    icon: Building2,
    color: 'bg-gray-600',
    description: t('usefulInfoMunicipalityDesc'),
    hours: t('usefulInfoHoursWeekdays')
  },
  {
    id: 'tourist_org',
    name: t('usefulInfoTouristOrg'),
    number: '2810 246100',
    address: 'Crete HQ',
    icon: MapPin,
    color: 'bg-cyan-500',
    description: t('usefulInfoTouristOrgDesc'),
    hours: t('usefulInfoHoursWeekdays')
  }
];

const getImportantNumbers = (t) => [
  {
    id: 'guesthouse',
    name: t('usefulInfoGuesthouse'),
    number: '+49 172 6974288',
    icon: MapPin,
    color: 'bg-purple-500',
    description: t('usefulInfoGuesthouseDesc'),
    available: t('usefulInfoGuesthouseAvailable')
  }
];

export default function UsefulInformation() {
  const { t } = useLanguage();
  
  const emergencyContacts = getEmergencyContacts(t);
  const localServices = getLocalServices(t);
  const importantNumbers = getImportantNumbers(t);
  
  const callNumber = (number) => {
    window.location.href = `tel:${number}`;
  };

  const renderContactCard = (contact, showHours = false) => (
    <Card key={contact.id} className="h-full hover:shadow-lg transition-shadow duration-300">
      <CardContent className="p-6 h-full flex flex-col">
        <div className="flex items-start gap-4 flex-grow">
          <div className={`${contact.color} p-3 rounded-full flex-shrink-0`}>
            <contact.icon className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1 min-w-0 flex flex-col h-full">
            <div className="flex-grow">
              <h3 className="font-bold text-lg text-gray-900 mb-1">
                {contact.name}
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                {contact.description}
              </p>
              
              {contact.address && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>{contact.address}</span>
                </div>
              )}
              
              {(contact.hours || contact.available) && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>{contact.hours || contact.available}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Buttons centered across full card width - outside the icon/text layout */}
        <div className="flex flex-col items-center space-y-2 mt-auto pt-4">
          <Button
            onClick={() => callNumber(contact.number)}
            className="bg-gray-900 hover:bg-gray-800 text-white inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium w-48"
            size="sm"
          >
            <Phone className="w-4 h-4 mr-2" />
            {contact.number}
          </Button>
          
          {contact.international && (
            <Button
              onClick={() => callNumber(contact.international)}
              variant="outline"
              className="inline-flex items-center justify-center px-3 py-1 rounded-md text-xs font-medium border border-gray-300 hover:bg-gray-50 w-48"
              size="sm"
            >
              <Phone className="w-3 h-3 mr-2" />
              International: {contact.international}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('usefulInfoTitle')}</h2>
        <p className="text-xl text-gray-600">
          {t('usefulInfoSubtitle')}
        </p>
      </div>
      
      {/* Emergency Services */}
      <div className="mb-12">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-7 h-7 text-red-500" />
          {t('usefulInfoEmergencyServices')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {emergencyContacts.map(contact => renderContactCard(contact))}
        </div>
      </div>
      
      {/* Local Services */}
      <div className="mb-12">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <Cross className="w-7 h-7 text-green-500" />
          {t('usefulInfoLocalServices')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {localServices.map(contact => renderContactCard(contact, true))}
        </div>
      </div>
      
      {/* Important Numbers */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <Phone className="w-7 h-7 text-purple-500" />
          {t('usefulInfoYourAccommodation')}
        </h3>
        <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
          {importantNumbers.map(contact => renderContactCard(contact))}
        </div>
      </div>
      
      {/* Additional Information */}
      <div className="bg-blue-50 rounded-2xl p-6 border-l-4 border-blue-500">
        <h4 className="font-bold text-lg text-blue-900 mb-3">{t('usefulInfoImportantNotes')}</h4>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
            <span>{t('usefulInfoNote1')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
            <span>{t('usefulInfoNote2')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
            <span>{t('usefulInfoNote3')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
            <span>{t('usefulInfoNote4')}</span>
          </li>
        </ul>
      </div>
    </section>
  );
}
